import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { db } from '../../../api/firebase/config';
import CustomLoading from '../../uiParts/form/CustomLoading';
import EmailInboxEditDialog from './EmailInboxEditDialog';
import { useCompanyId } from '../../../hooks/useCompanyId';
import { usePatternList } from '../../../hooks/usePatternList';
import {
  updateEmailInboxSettings,
  deleteEmailInboxAddress,
} from '../../../api/firebase/function/emailInboxFunction';
import { EmailInbox } from '../../../types/emailImport';
import { emailInboxType } from '../../../constants/enum';
import {
  gray40,
  gray60,
  gray80,
  gray100,
  primaryButtonBackgroundColor,
  red20,
  red50,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import {
  dialogText,
  dialogTitle,
  regularMd,
  regularSm,
  sectionTitle,
} from '../../../constants/location';

const styles = {
  meta: {
    ...regularSm,
    color: gray80,
    padding: '16px 0',
  },
  // 受信トレイ画面（EmailMessageListPage）の履歴なし表示とスタイルを統一（アイコン＋太字タイトル＋説明）
  emptyWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    padding: '64px 16px',
    textAlign: 'center' as const,
  },
  emptyIcon: {
    color: gray60,
    height: '56px',
    marginBottom: '24px',
    width: '56px',
  },
  emptyTitle: {
    ...sectionTitle,
    marginBottom: '8px',
  },
  emptyDescription: {
    ...regularMd,
    color: gray80,
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    width: '100%',
    WebkitOverflowScrolling: 'touch',
  },
  table: {
    minWidth: '880px',
    width: '100%',
  },
  tableHeadRow: {
    height: '48px',
  },
  tableHeadText: {
    ...regularSm,
    color: gray80,
    padding: '0 16px',
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
  },
  tableDataRow: {
    height: '48px',
  },
  tableDataText: {
    ...regularSm,
    color: gray100,
    padding: '0 16px',
    whiteSpace: 'nowrap' as const,
  },
  addressCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  address: {
    ...regularSm,
    color: gray100,
    wordBreak: 'break-all' as const,
  },
  senderText: {
    ...regularSm,
    color: gray100,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    maxWidth: '280px',
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusText: {
    ...regularMd,
    color: gray100,
    minWidth: '40px',
  },
  actionCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    whiteSpace: 'nowrap' as const,
  },
  editButton: {
    height: '32px',
  },
  // グループ管理（GroupListCard）と同様に、編集ボタンとの間に 16px の余白を取る
  deleteButton: {
    height: '32px',
    marginLeft: '16px',
  },
  // 削除確認ダイアログ（design-rules.md「8. ダイアログ」準拠）
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      width: '480px',
      '@media screen and (max-width:960px)': {
        padding: '32px 16px',
      },
    },
  },
  dialogHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  dialogTitle,
  dialogBodyText: {
    ...dialogText,
    marginBottom: '24px',
    wordBreak: 'break-all' as const,
  },
  dialogErrorBox: {
    backgroundColor: red20,
    border: `1px solid ${red50}`,
    borderRadius: '4px',
    color: red50,
    fontSize: '14px',
    padding: '8px 12px',
    marginBottom: '24px',
  },
  dialogActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  dialogActionButton: {
    height: '44px',
    minWidth: '120px',
  },
};

interface Props {
  /** 操作結果の通知（スナックバーはページ側で表示） */
  onNotify: (message: string, isError?: boolean) => void;
  /** 値が変わると一覧を再読み込みする（発行後の反映用） */
  reloadKey?: number;
}

/**
 * メール取り込みの発行済み専用アドレス一覧（テーブル）。
 * パターン名・アドレス・許可送信元を表示し、有効/無効・許可送信元編集・削除を行う。
 */
const EmailInboxList: React.FC<Props> = ({ onNotify, reloadKey }) => {
  const { companyId } = useCompanyId();
  const { patterns } = usePatternList();

  const [inboxes, setInboxes] = useState<EmailInbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editTarget, setEditTarget] = useState<EmailInbox | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmailInbox | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const loadInboxes = useCallback(async (): Promise<void> => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const q = query(
        collection(db, 'email_inboxes'),
        where('company_id', '==', companyId),
        orderBy('created_at', 'desc'),
      );
      const snap = await getDocs(q);
      setInboxes(snap.docs.map(d => ({ ...(d.data() as EmailInbox), id: d.id })));
    } catch (e) {
      Sentry.captureException(e);
      setError('専用アドレスの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void loadInboxes();
  }, [loadInboxes, reloadKey]);

  const handleToggleEnabled = async (inbox: EmailInbox): Promise<void> => {
    if (!companyId) return;
    const next = !inbox.is_enabled;
    // 楽観的更新: 先にUIを切り替え、失敗時のみ元に戻す
    setInboxes(prev => prev.map(i => (i.id === inbox.id ? { ...i, is_enabled: next } : i)));
    try {
      await updateEmailInboxSettings({
        company_id: companyId,
        inbox_id: inbox.id,
        is_enabled: next,
      });
      onNotify(`専用アドレスを${next ? '有効化' : '無効化'}しました。`);
    } catch (e) {
      Sentry.captureException(e);
      setInboxes(prev => prev.map(i => (i.id === inbox.id ? { ...i, is_enabled: !next } : i)));
      onNotify('有効/無効の切り替えに失敗しました。', true);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!companyId || !deleteTarget) return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteEmailInboxAddress({ company_id: companyId, inbox_id: deleteTarget.id });
      setDeleteTarget(null);
      await loadInboxes();
      onNotify('専用アドレスを削除しました。');
    } catch (e) {
      Sentry.captureException(e);
      setDeleteError('削除に失敗しました。');
    } finally {
      setDeleting(false);
    }
  };

  const handleCopy = (address: string): void => {
    void navigator.clipboard?.writeText(address);
    onNotify('アドレスをコピーしました。');
  };

  const patternName = (inbox: EmailInbox): string => {
    if (inbox.inbox_type !== emailInboxType.pattern) return '自動仕分';
    return patterns.find(p => p.patternId === inbox.pattern_id)?.pattern_name ?? '-';
  };

  if (loading) return <CustomLoading loading />;
  if (error) return <Typography sx={styles.meta}>{error}</Typography>;
  if (inboxes.length === 0) {
    return (
      <Box sx={styles.emptyWrapper}>
        <AlternateEmailIcon sx={styles.emptyIcon} />
        <Typography sx={styles.emptyTitle}>発行済みのアドレスはありません</Typography>
        <Typography sx={styles.emptyDescription}>
          右上の「アドレス発行」から専用アドレスを発行しましょう。
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={styles.tableWrapper}>
        <Table sx={styles.table}>
          <TableHead>
            <TableRow sx={styles.tableHeadRow}>
              <TableCell sx={styles.tableHeadText}>受信アドレス名</TableCell>
              <TableCell sx={styles.tableHeadText}>読取パターン名</TableCell>
              <TableCell sx={styles.tableHeadText}>アドレス</TableCell>
              <TableCell sx={styles.tableHeadText}>許可送信元</TableCell>
              <TableCell sx={styles.tableHeadText}>ステータス</TableCell>
              <TableCell sx={styles.tableHeadText}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inboxes.map(inbox => {
              const senders = (inbox.allowed_senders ?? []).join(', ');
              return (
                <TableRow key={inbox.id} sx={styles.tableDataRow}>
                  <TableCell sx={styles.tableDataText}>{inbox.name || '-'}</TableCell>
                  <TableCell sx={styles.tableDataText}>{patternName(inbox)}</TableCell>
                  <TableCell sx={styles.tableDataText}>
                    <Box sx={styles.addressCell}>
                      <Typography sx={styles.address}>{inbox.address}</Typography>
                      <IconButton size="small" onClick={(): void => handleCopy(inbox.address)}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell sx={styles.tableDataText}>
                    <Tooltip title={senders || '-'}>
                      <Box component="span" sx={styles.senderText}>
                        {senders || '-'}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={styles.tableDataText}>
                    <Box sx={styles.statusCell}>
                      <Switch
                        checked={inbox.is_enabled}
                        onChange={(): void => void handleToggleEnabled(inbox)}
                      />
                      <Typography sx={styles.statusText}>
                        {inbox.is_enabled ? '有効' : '無効'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={styles.tableDataText}>
                    <Box sx={styles.actionCell}>
                      <Button
                        onClick={(): void => setEditTarget(inbox)}
                        sx={[secondaryButtonBackgroundColor, styles.editButton]}
                      >
                        編集
                      </Button>
                      <Button
                        onClick={(): void => setDeleteTarget(inbox)}
                        sx={[tertiaryButtonBackgroundColor, styles.deleteButton]}
                      >
                        削除
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {/* アドレス編集ダイアログ（名称・許可送信元） */}
      <EmailInboxEditDialog
        open={editTarget !== null}
        inbox={editTarget}
        onClose={(): void => setEditTarget(null)}
        onSaved={(): void => void loadInboxes()}
        onNotify={onNotify}
      />

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteTarget !== null}
        onClose={(): void => {
          if (!deleting) setDeleteTarget(null);
        }}
        sx={styles.dialog}
      >
        <Box sx={styles.dialogHeader}>
          <Typography sx={styles.dialogTitle}>専用アドレスを削除しますか？</Typography>
          <IconButton onClick={(): void => setDeleteTarget(null)} disabled={deleting} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography sx={styles.dialogBodyText}>
          「{deleteTarget?.address}
          」を削除します。このアドレス宛に届いたメールは取り込まれなくなります。
        </Typography>
        {deleteError && <Box sx={styles.dialogErrorBox}>{deleteError}</Box>}
        <Box sx={styles.dialogActions}>
          <Button
            onClick={(): void => setDeleteTarget(null)}
            disabled={deleting}
            sx={[tertiaryButtonBackgroundColor, styles.dialogActionButton]}
          >
            キャンセル
          </Button>
          <Button
            onClick={(): void => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={[primaryButtonBackgroundColor, styles.dialogActionButton]}
          >
            削除する
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default EmailInboxList;
